/**
 * Who may do what inside one tenant.
 *
 * The role lives in `tenants/{tenantId}/members/{uid}`, not in a custom claim.
 * A claim is capped at 1000 bytes and refreshes only when the ID token does —
 * up to an hour — which for a person invited to a second tenant means an hour of
 * being told they have no access. A membership document is read by the security
 * rules on every request and is correct the moment it is written.
 *
 * The cost is a rules-side `get()` per request, billed as a read. It is the
 * right trade here: this app's read volume is a handful of animals per session.
 */
/**
 * `handler` is the person who does the day-to-day animal work — the breeder, in
 * this product's copy. The role names are species-neutral for the same reason
 * the paths are (see platform/src/paths.ts); the phrase tree is where they get
 * their words, and there `role.handler` reads "Hodowca".
 *
 * THERE WAS A FOURTH, `vet`, AND IT IS GONE. It sat between handler and viewer
 * and could write weights and health events but not litters — a distinction
 * nobody was drawing in practice, because a vet who is in a kennel's app at all
 * is there as a second pair of hands and was being invited as a handler anyway.
 * What the role was really being used for was the other direction: letting
 * somebody *see* part of the record without touching any of it. That is what
 * `viewer` and its scopes below are for now. Migration 002 turns every
 * membership and every unaccepted invitation that still says `vet` into
 * `handler`, which is the role that keeps everything a vet could already do.
 */
export const ROLES = ['owner', 'handler', 'viewer'] as const;

export type Role = (typeof ROLES)[number];

/** Ordered weakest to strongest; `atLeast` compares on this. */
const RANK: Record<Role, number> = {
  viewer: 0,
  handler: 1,
  owner: 2,
};

export const atLeast = (role: Role, required: Role): boolean => RANK[role] >= RANK[required];

/**
 * May record and correct weights and health events.
 *
 * The same answer as `canWriteLitters` today, and still a separate question:
 * with `vet` gone there is nobody left who may write a weighing but not a
 * litter, and the two predicates coincide. They are kept apart because the call
 * sites mean different things — correcting yesterday's weight and deleting the
 * record that a whole weighing happened are asked as two questions in
 * firestore.rules, and collapsing them here would quietly make one answer the
 * other if a role is ever added between these two again.
 */
export const canWriteHealth = (role: Role): boolean => role === 'owner' || role === 'handler';

export const canWriteLitters = (role: Role): boolean => role === 'owner' || role === 'handler';

export const canManageMembers = (role: Role): boolean => role === 'owner';

/**
 * A litter that is still only planned is a kennel's own business.
 *
 * An `incoming` litter is a mating that may not take — it names the dam, the
 * sire and a due date, and until the puppies are on the ground it is a plan and
 * not a record. A viewer is somebody outside the kennel who has been let in to
 * watch one thing; the plans are not that thing. Owners and handlers see every
 * status, which is the whole home screen.
 */
export const canSeePlannedLitters = (role: Role): boolean => role !== 'viewer';

// ---------------------------------------------------------------------------
// What a viewer sees
// ---------------------------------------------------------------------------

/**
 * The parts of a litter's record a viewer can be given, chosen one by one when
 * they are invited.
 *
 * A viewer is the role handed to somebody who is not running the kennel: the
 * family that bought the puppy, a co-owner of the dam, a friend following the
 * litter. What each of them is owed is different — the buyer wants the pictures,
 * the co-owner wants the weights — and before this the role was all-or-nothing,
 * so giving somebody the photographs also gave them every number in the growth
 * record.
 *
 *   measurements  the weighings: the growth chart, the table, the weighing log
 *   photos        the picture timeline and the log under it
 *   tasks         what is to be done to the litter, and what has been done
 *
 * AT LEAST ONE IS REQUIRED, and the invitation form insists — a viewer with no
 * scopes is an account that can sign in, see a litter's name and nothing else,
 * which is an invitation nobody meant to send.
 *
 * Scopes are stored on the membership (and copied onto it from the invitation),
 * and the security rules read them: see `canView` and firestore.rules. A
 * membership written before scopes existed carries none, and both sides read
 * that as all of them — which is exactly what such a viewer can see today.
 *
 * ADDING ONE IS A DENIAL TO EVERY EXISTING VIEWER, and that is the right
 * direction. `tasks` arrived after migration 002 had already written the field
 * down on every membership, so a viewer carrying `['measurements', 'photos']`
 * does not get the new section — nobody invited them to it. Only the memberships
 * still missing the field entirely fall through to the default and pick it up,
 * and those are the ones that never had a choice made about them at all.
 */
export const VIEW_SCOPES = ['measurements', 'photos', 'tasks'] as const;

export type ViewScope = (typeof VIEW_SCOPES)[number];

/**
 * Whether this person may read that part of the record.
 *
 * Only a viewer is scoped. Everyone else is in the kennel to run it and sees all
 * of it — which is why this takes the role as well as the scopes, rather than
 * being a set membership test the callers could write themselves and get wrong
 * for an owner whose membership happens to carry an empty array.
 */
export const canView = (
  role: Role,
  scopes: readonly ViewScope[],
  scope: ViewScope,
): boolean => role !== 'viewer' || scopes.includes(scope);
